import { Anchor, Box, Card, Popover, Stack, Text, Title } from '@mantine/core';
import { useState } from 'react';
import FacebookAudienceInfo from './Facebook/FacebookAudienceInfo';

const getStatusColor = (status) => {
  if (status === 'SUCCESS') return 'success-background-color';
  if (status === 'FAIL') return 'fail-background-color';
  if (status === 'LEARNING') return 'learning-background-color';
  return 'no-data-background-color';
};

const AdSetStatusVisualizer = ({ adSetStatuses }) => {
  const [opened, setOpened] = useState(false);

  if (!adSetStatuses || !adSetStatuses.statuses || adSetStatuses.statuses.length === 0) {
    return (
      <Popover position="bottom" withArrow shadow="md" opened={opened} onChange={setOpened}>
        <Popover.Target>
          <div
            className="statusCircle no-ad-data"
            style={{ cursor: 'pointer', display: 'inline-block' }}
            onMouseEnter={() => setOpened(true)}
            onMouseLeave={() => setOpened(false)}
          />
        </Popover.Target>
        <Popover.Dropdown>
          <Text size="sm">No Facebook Ad Set Details Found in Meta Ads Manager</Text>
        </Popover.Dropdown>
      </Popover>
    );
  }

  const primaryStatus = adSetStatuses.statuses[0];
  const hasIssues = adSetStatuses.statuses.some(s => s.issues_info && s.issues_info.length > 0);

  return (
    <Box style={{ display: 'inline-block' }}>
      <Popover position="bottom" withArrow shadow="md" width={400} opened={opened} onChange={setOpened}>
        <Popover.Target>
          <div
            className={`statusCircle ${getStatusColor(primaryStatus.learning_stage_info?.status)}`}
            style={{ cursor: 'pointer', display: 'inline-block', position: 'relative' }}
            onMouseEnter={() => setOpened(true)}
            onMouseLeave={() => setOpened(false)}
          >
            {hasIssues && (
              <span
                style={{
                  position: 'absolute',
                  top: '-2px',
                  right: '-2px',
                  width: '8px',
                  height: '8px',
                  backgroundColor: '#fa5252',
                  borderRadius: '50%',
                  border: '1px solid white'
                }}
              />
            )}
          </div>
        </Popover.Target>
      <Popover.Dropdown>
        <Stack gap="md">
          {/* Ad Set Details Section */}
          <Card withBorder p="sm">
            <Title order={6} mb="sm">Ad Set Details</Title>
            <Stack gap="xs">
              {adSetStatuses.statuses.map((status, index) => (
                <Box key={index}>
                  <Text size="sm">
                    <strong>Ad Set {status.id}</strong>
                  </Text>
                  <Text size="sm">
                    Learning Phase: <strong>{status.learning_stage_info?.status ?? 'UNKNOWN'}</strong>
                  </Text>
                  <Text size="sm">
                    Effective Status: <strong>{status.effective_status ?? 'UNKNOWN'}</strong>
                  </Text>

                  {status.issues_info && status.issues_info.length > 0 && (
                    <Box mt="xs" p="xs" style={{ backgroundColor: '#fff3cd', borderRadius: '4px' }}>
                      <Text size="sm" fw={600} c="orange">Issues Detected:</Text>
                      {status.issues_info.map((issue, idx) => (
                        <Box key={idx} mt="xs">
                          <Text size="xs" fw={600}>{issue.error_summary}</Text>
                          <Text size="xs" c="dimmed">{issue.error_message}</Text>
                          {issue.additional_info && (
                            <Text size="xs" c="dimmed" mt={4}>{issue.additional_info}</Text>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              ))}
              <Anchor
                href="https://www.facebook.com/business/help/112167992830700?id=561906377587030"
                target="_blank"
                rel="noreferrer"
                size="xs"
              >
                Learning Phase Guide
              </Anchor>
            </Stack>
          </Card>

          {/* Audience Details Section */}
          {adSetStatuses.audience && adSetStatuses.audience.info && (
            <FacebookAudienceInfo statuses={adSetStatuses} />
          )}

          {/* Product Set Details Section */}
          {adSetStatuses.productSetInfo && (
            <Card withBorder p="xs">
              <Title order={6} mb="xs">Product Set Details</Title>
              <Stack gap={4}>
                <Text size="xs">
                  <strong>Product Set ID:</strong> {adSetStatuses.productSetInfo.id}
                </Text>
                <Text size="xs">
                  <strong>Product Count:</strong> {adSetStatuses.productSetInfo.product_count}
                </Text>
                {adSetStatuses.productSetInfo.product_catalog && (
                  <>
                    <Text size="xs">
                      <strong>Catalog:</strong> {adSetStatuses.productSetInfo.product_catalog.name}
                    </Text>
                    <Text size="xs">
                      <strong>Catalog Products:</strong> {adSetStatuses.productSetInfo.product_catalog.product_count}
                    </Text>
                  </>
                )}
              </Stack>
            </Card>
          )}
        </Stack>
      </Popover.Dropdown>
      </Popover>
    </Box>
  );
};

export default AdSetStatusVisualizer;
